import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useDevSignIn } from "@/hooks/useDevSignIn";
import { supabase } from "@/integrations/supabase/client";
import { invokeWithRetry } from "@/lib/invokeWithRetry";
import { isProductionMode } from "@/lib/environment";
import { isPasswordAuthAllowedForProductionMode, isPasswordAuthAllowedSync } from "@/lib/authMode";
import { logger } from "@/lib/logger";
import { trackSsoLoginStartError } from "@/lib/ssoErrorTracker";
import { z } from "zod";
import { toast } from "sonner";
import { parseZodErrors, findProviderForDomain, type SsoProvider } from "@/lib/authSso";

/**
 * useAuthFlow (TES-848) — kapselt die gesamte imperative Auth-Logik der
 * Auth-Seite: State, Effekte (Redirect, E-Mail-Bestätigung, Dev-Mode-Check,
 * SSO-Provider-Laden, Domain-Restore, Bootstrap) und Handler (Login,
 * Passwort-Reset, Seed, Quick-Login, SSO-Login, Session-Check).
 *
 * VERHALTENSERHALTEND: aus Auth.tsx extrahiert, identische Logik/Reihenfolge.
 * Die Seite ist danach reine Komposition + Markup.
 */
export function useAuthFlow() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [quickLoggingIn, setQuickLoggingIn] = useState<string | null>(null);
  const [autoSeedStatus, setAutoSeedStatus] = useState<"idle" | "checking" | "done">("idle");
  const [isDevMode, setIsDevMode] = useState(false);
  const [isCheckingDevMode, setIsCheckingDevMode] = useState(true);
  // TES-2165: initialer Zustand ist NICHT pessimistisch `false`, sondern die beste
  // synchron verfuegbare Schaetzung (isPasswordAuthAllowedSync — VITE_PASSWORD_AUTH_MODE
  // "enabled"/"disabled" ist synchron-final, "auto" schaetzt "nicht production" bis der
  // echte check-dev-mode-Roundtrip unten antwortet). Grund: das Login-Formular + der davor
  // liegende OR-Divider haengen direkt an diesem Flag und liegen INNERHALB der auth-card
  // (data-testid). Ein pessimistischer Default `false` liess die Karte beim ersten Paint
  // leer und das komplette Formular erst nach dem Async-Fetch reinspringen -> CLS
  // (Belegfall: 284px-Sprung, TES-2165). In Production ist VITE_PASSWORD_AUTH_MODE explizit
  // "disabled" (.env.example.production) -> die Schaetzung ist dort synchron korrekt UND
  // final false, also kein Flash. In "auto" (nur dev/lokal, s. .env.example.development) ist
  // die Schaetzung optimistisch true; falls der echte Check production ergibt, korrigiert
  // sich das Flag danach (seltener Edge-Case, kein bekannter Deploy nutzt "auto" in Prod).
  const [passwordAuthAllowed, setPasswordAuthAllowed] = useState(() => isPasswordAuthAllowedSync());
  const { signIn, user } = useAuth();
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<SsoProvider[]>([]);
  const [ssoProvidersLoading, setSsoProvidersLoading] = useState(true);
  // SSO in-progress state for iframe/new tab flow
  const [ssoInProgress, setSsoInProgress] = useState(false);
  const [checkingSession, setCheckingSession] = useState(false);
  const [ssoEmail, setSsoEmail] = useState("");
  const [ssoEmailError, setSsoEmailError] = useState("");
  const [showSsoEmailInput, setShowSsoEmailInput] = useState(true);
  const [ssoDomain, setSsoDomain] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Dynamic validation schemas using t()
  const getLoginSchema = () =>
    z.object({
      email: z.string().email(t("auth.validation.invalidEmail")),
      password: z.string().min(6, t("auth.validation.passwordMin6")),
    });

  const getResetPasswordSchema = () =>
    z.object({
      email: z.string().email(t("auth.validation.invalidEmail")),
    });

  // Pre-fill email from URL params (for user switching)
  const prefillEmail = searchParams.get("email") || "";

  // Login form state
  const [loginEmail, setLoginEmail] = useState(prefillEmail);
  const [loginPassword, setLoginPassword] = useState("");

  useEffect(() => {
    // Handle redirect after login (e.g., from image proxy login page)
    if (user) {
      const redirectUrl = searchParams.get("redirect");

      if (redirectUrl) {
        try {
          const url = new URL(redirectUrl);

          // Security: only allow same-origin redirects (TES-819)
          if (url.origin === window.location.origin) {
            // Redirect-origin check PASSED — log origin only, never query/token params.
            logger.info("Redirect-origin check passed", { origin: url.origin });
            window.location.href = redirectUrl;
            return;
          } else {
            // Redirect-origin check FAILED — log the untrusted origin only.
            logger.warn("Redirect blocked (untrusted origin)", { origin: url.origin });
          }
        } catch {
          // Invalid URL, ignore and navigate to home
          logger.warn("Redirect blocked (invalid URL)");
        }
      }

      navigate("/");
    }
  }, [user, navigate, searchParams]);

  // Update login email when URL param changes
  useEffect(() => {
    if (prefillEmail) {
      setLoginEmail(prefillEmail);
    }
  }, [prefillEmail]);

  // Handle email confirmation from Supabase magic link
  useEffect(() => {
    const handleEmailConfirmation = async () => {
      const tokenHash = searchParams.get("token_hash");
      const type = searchParams.get("type");

      if (tokenHash && type === "email") {
        setIsLoading(true);
        try {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: "email",
          });

          if (error) {
            logger.error("Email confirmation error:", error);
            toast.error(t("auth.emailConfirmationFailed"), {
              description: error.message,
            });
          } else if (data.session) {
            toast.success(t("auth.emailConfirmed"), {
              description: t("auth.emailConfirmedDesc"),
            });
            navigate("/");
          } else {
            // Token verified but no session - user can now login
            toast.success(t("auth.emailConfirmed"), {
              description: t("auth.emailConfirmedLoginNow"),
            });
          }
        } catch (err) {
          logger.error("Email confirmation exception:", err);
          toast.error(t("auth.emailConfirmationFailed"));
        } finally {
          setIsLoading(false);
        }
      }
    };

    handleEmailConfirmation();
  }, [searchParams, navigate, t]);

  // Separate useEffect for Dev-Mode-Check (always execute on mount)
  useEffect(() => {
    const checkDevMode = async () => {
      setIsCheckingDevMode(true);
      try {
        // Use centralized environment utility
        const isProd = await isProductionMode();
        setIsDevMode(!isProd);
        setPasswordAuthAllowed(isPasswordAuthAllowedForProductionMode(isProd));
      } catch (error) {
        logger.error("Error in dev mode check:", error);
      } finally {
        setIsCheckingDevMode(false);
      }
    };

    checkDevMode();
  }, []);

  // Load active SSO providers on mount (via Edge Function to bypass RLS)
  useEffect(() => {
    const fetchSsoProviders = async () => {
      setSsoProvidersLoading(true);
      // Entry log — no parameters carry PII.
      logger.debug("SSO provider fetch started");
      try {
        const { data, error } = await supabase.functions.invoke("get-sso-providers");

        if (error) {
          logger.error("SSO provider fetch failed", { message: error.message });
          return;
        }

        if (data?.error) {
          logger.error("SSO provider fetch edge-function error", { message: data.error });
          return;
        }

        // Intentional: SSO providers are optional — not every tenant has SSO configured
        const providers = data?.providers ?? [];
        setSsoProviders(providers);
        // Exit log — provider COUNT only, never provider secrets/config.
        logger.debug("SSO provider fetch complete", { count: providers.length });
      } catch (error) {
        logger.error("SSO provider fetch exception", {
          message: error instanceof Error ? error.message : String(error),
        });
      } finally {
        setSsoProvidersLoading(false);
      }
    };

    fetchSsoProviders();
  }, []);

  // Restore last SSO domain from localStorage
  useEffect(() => {
    const savedDomain = localStorage.getItem("lastSsoDomain");
    if (savedDomain) {
      setSsoDomain(savedDomain);
      setShowSsoEmailInput(false);
    }
    // Clean up legacy key
    localStorage.removeItem("lastSsoEmail");
  }, []);

  // Auto-seed dev users on first load if no auth users exist
  // Bootstrap check - the backend handles all seeding automatically
  useEffect(() => {
    // fallow-ignore-next-line complexity -- TES-848: unverändert aus Auth.tsx verschoben (Komplexität nicht erhöht)
    const checkBootstrapStatus = async () => {
      if (user) return;

      setAutoSeedStatus("checking");
      logger.debug("Bootstrap status check started");

      try {
        const { data: devStatus, error: devStatusError } = await invokeWithRetry<{
          isProductionMode?: boolean;
          bootstrapExecuted?: boolean;
          bootstrapResults?: {
            rolePermissions: number;
            roleAssignability: number;
            profilesCreated: number;
            devUsersCreated: number;
          };
        }>("check-dev-mode");

        if (devStatusError) {
          logger.error("Bootstrap status check failed", { message: devStatusError.message });
          return;
        }

        // Bootstrap status — booleans/counts only, no user identifiers.
        logger.info("Bootstrap status resolved", {
          bootstrapExecuted: !!devStatus?.bootstrapExecuted,
        });

        // Backend automatically handles bootstrap when needed
        if (devStatus?.bootstrapExecuted) {
          const results = devStatus.bootstrapResults;
          const devUsersCreated = results?.devUsersCreated ?? 0;

          if (devUsersCreated > 0) {
            // Dev users were created
            toast.success(t("auth.testUsersAutoCreated"), {
              description: t("auth.testUsersAutoCreatedDesc"),
            });
          } else if ((results?.rolePermissions ?? 0) > 0 || (results?.profilesCreated ?? 0) > 0) {
            // System was initialized (RLS data + profiles healed)
            toast.success(t("auth.systemInitialized"), {
              description: t("auth.systemInitializedDesc"),
            });
          }
        }
      } catch (error) {
        logger.error("Bootstrap status check exception", {
          message: error instanceof Error ? error.message : String(error),
        });
      } finally {
        setAutoSeedStatus("done");
      }
    };

    checkBootstrapStatus();
  }, [user, t]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!passwordAuthAllowed) {
      toast.error(t("auth.passwordLoginDisabledTitle"), {
        description: t("auth.passwordLoginDisabledDesc"),
      });
      return;
    }

    try {
      getLoginSchema().parse({ email: loginEmail, password: loginPassword });
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors(parseZodErrors(err));
        return;
      }
    }

    setIsLoading(true);
    await signIn(loginEmail, loginPassword);

    // Navigation is handled by the useEffect above when user state changes
    // No need for explicit navigation here

    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      getResetPasswordSchema().parse({ email: resetEmail });
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors(parseZodErrors(err, "reset_"));
        return;
      }
    }

    setIsLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/auth?reset=true`,
    });

    setIsLoading(false);

    if (error) {
      toast.error(t("auth.resetEmailError"), {
        description: error.message,
      });
    } else {
      setResetEmailSent(true);
      toast.success(t("auth.resetEmailSuccess"), {
        description: t("auth.resetEmailSuccessDesc"),
      });
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setResetEmail("");
    setResetEmailSent(false);
    setErrors({});
  };

  // fallow-ignore-next-line complexity -- TES-848: unverändert aus Auth.tsx verschoben (Komplexität nicht erhöht)
  const handleSeedDevUsers = async () => {
    setIsSeeding(true);
    try {
      const { data, error } = await supabase.functions.invoke("seed-dev-users");

      if (error) throw error;

      if (data.success) {
        toast.success(t("auth.testUsersCreated"), {
          description: data.message,
        });
      } else {
        toast.error(t("common.error"), {
          description: data.error || "Unbekannter Fehler",
        });
      }
    } catch (error) {
      logger.error("Error seeding dev users", {
        message: error instanceof Error ? error.message : String(error),
      });
      toast.error(t("auth.createUsersError"));
    } finally {
      setIsSeeding(false);
    }
  };

  const { devSignIn } = useDevSignIn();

  const handleQuickLogin = async (email: string) => {
    setQuickLoggingIn(email);
    try {
      const { error } = await devSignIn(email);

      if (error) {
        toast.error(t("auth.quickLoginFailed"), {
          description: t("auth.quickLoginFailedDesc"),
        });
      }
    } catch (error) {
      logger.error("Quick login error", {
        message: error instanceof Error ? error.message : String(error),
      });
      toast.error(t("auth.quickLoginFailed"));
    } finally {
      setQuickLoggingIn(null);
    }
  };

  const handleSsoEmailLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setSsoEmailError("");

    const email = ssoEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      setSsoEmailError(t("auth.ssoLoginHint"));
      return;
    }

    const domain = email.split("@")[1];

    // Find matching provider by domain
    const matchingProvider = findProviderForDomain(domain, ssoProviders);

    if (!matchingProvider?.supabase_provider_id) {
      setSsoEmailError(t("auth.ssoNotConfiguredDesc"));
      return;
    }

    // Save domain to localStorage for next visit
    localStorage.setItem("lastSsoDomain", domain);
    setSsoDomain(domain);

    await performSsoLogin(matchingProvider.supabase_provider_id);
  };

  // fallow-ignore-next-line complexity -- TES-848: unverändert aus Auth.tsx verschoben (Komplexität nicht erhöht)
  const performSsoLogin = async (providerId: string) => {
    // Check if we're in an iframe (e.g., Lovable editor preview)
    const isEmbedded = window.self !== window.top;

    // IMMEDIATELY set SSO in-progress state for embedded scenarios
    if (isEmbedded) {
      setSsoInProgress(true);
    }
    setSsoLoading(true);

    // Preserve original redirect URL through SSO flow (for Teams deeplinks etc.)
    const originalRedirect = searchParams.get("redirect");
    let callbackUrl = `${window.location.origin}/auth/callback`;
    if (originalRedirect) {
      callbackUrl += `?originalRedirect=${encodeURIComponent(originalRedirect)}`;
    }

    try {
      const { data, error } = await supabase.auth.signInWithSSO({
        providerId,
        options: {
          redirectTo: callbackUrl,
          skipBrowserRedirect: true,
        },
      });

      if (error) {
        if (isEmbedded) setSsoInProgress(false);

        trackSsoLoginStartError(providerId, error.message, {
          errorCode: error.code,
          status: error.status,
        });

        toast.info(t("auth.ssoNotConfigured"), {
          description: t("auth.ssoNotConfiguredDesc"),
        });
        return;
      }

      if (data?.url) {
        if (isEmbedded) {
          // Open in new tab when embedded in iframe
          const newWindow = window.open(data.url, "_blank", "noopener,noreferrer");
          if (!newWindow) {
            setSsoInProgress(false);
            toast.error(t("auth.oauth.popupBlockedTitle"), {
              description: t("auth.oauth.popupBlockedDesc"),
            });
          }
          // ssoInProgress stays true - UI shows "I'm back" button
        } else {
          // Normal redirect when not in iframe
          window.location.href = data.url;
        }
      } else {
        if (isEmbedded) setSsoInProgress(false);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error("SSO login error:", error);

      // Track unexpected SSO errors in Sentry
      trackSsoLoginStartError(providerId, errorMessage, {
        errorType: "exception",
      });

      if (isEmbedded) setSsoInProgress(false);
      toast.error(t("auth.ssoLoginFailed"));
    } finally {
      setSsoLoading(false);
    }
  };

  const handleCheckSession = async () => {
    setCheckingSession(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        // User is logged in! Redirect to home
        toast.success(t("auth.oauth.loginSuccessTitle"), {
          description: t("auth.oauth.loginSuccessDesc"),
        });
        setSsoInProgress(false);
        navigate("/");
      } else {
        toast.error(t("auth.oauth.sessionNotFound"), {
          description: t("auth.oauth.sessionNotFoundDesc"),
        });
      }
    } catch (error) {
      logger.error("Session check error", {
        message: error instanceof Error ? error.message : String(error),
      });
      toast.error(t("common.error"));
    } finally {
      setCheckingSession(false);
    }
  };

  const handleCancelSso = () => {
    setSsoInProgress(false);
  };

  return {
    t,
    // state
    isLoading,
    errors,
    showForgotPassword,
    setShowForgotPassword,
    resetEmail,
    setResetEmail,
    resetEmailSent,
    isSeeding,
    quickLoggingIn,
    autoSeedStatus,
    isDevMode,
    isCheckingDevMode,
    passwordAuthAllowed,
    ssoLoading,
    ssoProviders,
    ssoProvidersLoading,
    ssoInProgress,
    checkingSession,
    ssoEmail,
    setSsoEmail,
    ssoEmailError,
    setSsoEmailError,
    showSsoEmailInput,
    setShowSsoEmailInput,
    ssoDomain,
    setSsoDomain,
    loginEmail,
    setLoginEmail,
    loginPassword,
    setLoginPassword,
    // handlers
    handleLogin,
    handleForgotPassword,
    handleBackToLogin,
    handleSeedDevUsers,
    handleQuickLogin,
    handleSsoEmailLogin,
    performSsoLogin,
    handleCheckSession,
    handleCancelSso,
  };
}
